import { useCallback } from 'react';

import { CustomToastItem, useToasts } from '@air/provider-toast';

import { ShowcaseActionConfirmationToastContent } from '~/components/ShowcaseModal/components/ShowcaseActionConfirmationToastContent';
import { type ShowcaseCreated } from '~/components/ShowcaseModal/hooks/useCreateShowcaseModalState';
import { useNavigateToShowcaseEdit } from '~/hooks/useNavigateToShowcaseEdit';
import { getFullShareUrl } from '~/utils/shareUrl';
import { buildShareUrlCopiedAnalyticsParams } from '~/utils/shortUrl/buildShareUrlCopiedAnalyticsParams';

const TOAST_AUTO_DISMISS_MS = 5_000;

export type UseShowcaseCreatedToastParams = {
  /**
   * Closes the create modal. The toast's "Edit" action leaves for the editor, and the modal can
   * still be open at that point (the team-invite nudge keeps it open past creation), so Edit closes
   * it rather than stranding it over the editor.
   */
  closeCreateShowcaseModal: () => void;
};

/**
 * The confirmation shown by entry points that create a showcase from items the user already had in
 * hand (the library share menu, the selection bar): they leave the user in their content, so the
 * toast is the confirmation and its "Edit" action is the way over to the editor.
 */
export const useShowcaseCreatedToast = ({ closeCreateShowcaseModal }: UseShowcaseCreatedToastParams) => {
  const { showToast } = useToasts();
  const { navigateToShowcaseEdit } = useNavigateToShowcaseEdit();

  const showShowcaseCreatedToast = useCallback(
    ({ collectionId, shortUrl }: ShowcaseCreated) => {
      const shareUrl = getFullShareUrl(shortUrl.id);

      showToast(
        ({ onDismiss }) => (
          <ShowcaseActionConfirmationToastContent
            message="New showcase created"
            shareUrl={shareUrl}
            onDismiss={onDismiss}
            copyTracking={{
              id: collectionId,
              shareId: shortUrl.id,
              type: 'collection',
              location: 'share-collection-edit-modal',
              ...buildShareUrlCopiedAnalyticsParams({
                shareId: shortUrl.id,
                shortUrl,
                objectType: 'collection',
              }),
            }}
            // The destination (new editor vs the internal collection page) is owned by the shared
            // navigation hook.
            onEdit={() => {
              closeCreateShowcaseModal();
              navigateToShowcaseEdit(collectionId);
            }}
          />
        ),
        {
          timeBeforeAutoDismiss: TOAST_AUTO_DISMISS_MS,
          CustomContainer: CustomToastItem,
        },
      );
    },
    [closeCreateShowcaseModal, navigateToShowcaseEdit, showToast],
  );

  return { showShowcaseCreatedToast };
};
